import { create } from 'zustand';

const TURN_KEY = 'indusketch.vr.smoothTurn';

interface VrState {
  /** The browser can show immersive VR (a headset is there). */
  readonly supported: boolean;
  /** The page runs in a headset's own browser (Quest, Pico): VR is offered right away. */
  readonly headset: boolean;
  /** Turning with the right stick: in 30° steps or smoothly. */
  readonly smoothTurn: boolean;
  /** The menu in the headset is open. */
  readonly menuOpen: boolean;
  /** Short message shown in the headset (e.g. why a game cannot start). */
  readonly notice: string | null;
  /** The suggestion was put away for this visit. */
  readonly dismissed: boolean;
  setSmoothTurn: (on: boolean) => void;
  setMenuOpen: (open: boolean) => void;
  setNotice: (notice: string | null) => void;
  dismiss: () => void;
}

const storedTurn = (): boolean => {
  try {
    return localStorage.getItem(TURN_KEY) === '1';
  } catch {
    return false;
  }
};

export const isHeadsetBrowser = (userAgent: string): boolean =>
  /OculusBrowser|Quest|Pico/i.test(userAgent);

export const useVrStore = create<VrState>()((set) => ({
  supported: false,
  headset: typeof navigator !== 'undefined' && isHeadsetBrowser(navigator.userAgent),
  smoothTurn: storedTurn(),
  menuOpen: false,
  notice: null,
  dismissed: false,
  setSmoothTurn: (smoothTurn) => {
    try {
      localStorage.setItem(TURN_KEY, smoothTurn ? '1' : '0');
    } catch {
      // Private mode: the choice is just not remembered.
    }
    set({ smoothTurn });
  },
  setMenuOpen: (menuOpen) => set({ menuOpen, notice: null }),
  setNotice: (notice) => set({ notice }),
  dismiss: () => set({ dismissed: true }),
}));

type XRSystem = { isSessionSupported: (mode: string) => Promise<boolean> };

/** Asks the browser whether immersive VR works. */
export function detectVr(): void {
  if (typeof navigator === 'undefined') return;
  const xr = (navigator as { xr?: XRSystem }).xr;
  void xr
    ?.isSessionSupported('immersive-vr')
    .then((supported) => useVrStore.setState({ supported }))
    .catch(() => undefined);
}

detectVr();
